import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Authenticator,
  ThemeProvider,
  translations,
  useAuthenticator,
} from '@aws-amplify/ui-react'
import { confirmSignIn, signIn, signOut } from 'aws-amplify/auth'
import { I18n } from 'aws-amplify/utils'
import i18n from '@/i18n'
import { useAuth, useMunicipalityFromHost } from '@/hooks'
import { authApi, getErrorMessage, isHandledAuthError } from '@/services'
import { saveSessionVersion } from '@/services/auth/localDev'

I18n.putVocabularies(translations)

function syncAmplifyLocale() {
  const lang = i18n.language?.startsWith('ja') ? 'ja' : 'en'
  I18n.setLanguage(lang)

  if (lang === 'ja') {
    const v = i18n.t('auth:amplify', { returnObjects: true }) as Record<string, string>
    I18n.putVocabularies({
      ja: {
        'Sign In': v.signIn,
        'Sign in': v.signIn,
        'Signing in': v.signingIn,
        'Username': v.username,
        'Enter your Username': v.enterUsername,
        'Password': i18n.t('auth:password'),
        'Enter your Password': v.enterPassword,
        'Forgot your password?': v.forgotPassword,
        'Reset Password': v.resetPassword,
        'Change Password': i18n.t('auth:changePassword'),
        'New Password': v.newPasswordLabel,
        'Enter your new password': v.enterNewPassword,
        'Confirm Password': v.confirmPasswordLabel,
        'Please confirm your Password': v.pleaseConfirmPassword,
        'Send code': v.sendCode,
        'Back to Sign In': v.backToSignIn,
        'Confirm': v.confirm,
        'Submit': v.submit,
        'Code': v.code,
        'Enter your code': v.enterCode,
        'Password did not conform with policy: Password must have symbol characters': 'パスワードに記号を含めてください。',
        'Password did not conform with policy: Password must have uppercase characters': 'パスワードに英大文字を含めてください。',
        'Password did not conform with policy: Password must have lowercase characters': 'パスワードに英小文字を含めてください。',
        'Password did not conform with policy: Password must have numeric characters': 'パスワードに数字を含めてください。',
        'Password did not conform with policy: Password not long enough': 'パスワードは8文字以上で入力してください。',
        'Password does not conform to policy: Password must have symbol characters': 'パスワードに記号を含めてください。',
        'Password does not conform to policy: Password must have uppercase characters': 'パスワードに英大文字を含めてください。',
        'Password does not conform to policy: Password must have lowercase characters': 'パスワードに英小文字を含めてください。',
        'Password does not conform to policy: Password must have numeric characters': 'パスワードに数字を含めてください。',
        'Password does not conform to policy: Password not long enough': 'パスワードは8文字以上で入力してください。',
        'Incorrect username or password.': 'ログインIDまたはパスワードが正しくありません。',
        'User does not exist.': 'ログインIDまたはパスワードが正しくありません。',
        'Password attempts exceeded': '試行回数が上限に達しました。しばらくしてから再度お試しください。',
        'User is disabled.': 'このアカウントは無効化されています。管理者にお問い合わせください。',
        'Username/client id combination not found.': 'ログインIDまたはパスワードが正しくありません。',
        'Confirmation code cannot be empty': '確認コードを入力してください。',
        'Invalid verification code provided, please try again.': '確認コードが正しくありません。',
        'Invalid code provided, please request a code again.': '確認コードが正しくありません。再度コードをリクエストしてください。',
        'Attempt limit exceeded, please try after some time.': '試行回数が上限に達しました。しばらくしてから再度お試しください。',
        'Your passwords must match': 'パスワードが一致しません。',
        'Password must have at least 8 characters': 'パスワードは8文字以上で入力してください。',
        'Invalid attributes given, email is missing': 'メールアドレスが未登録のため、初回パスワード変更を完了できません。管理者にメールアドレスの登録を依頼してください。',
        'Invalid session for the user, session is expired.': 'セッションの有効期限が切れました。再度ログインしてください。',
        'Invalid session for the user.': 'セッションが無効です。再度ログインしてください。',
        'Temporary password has expired and must be reset by an administrator.': '初期パスワードの有効期限が切れました。管理者に再発行を依頼してください。',
      },
    })
  }
}

syncAmplifyLocale()
i18n.on('languageChanged', syncAmplifyLocale)

// Amplify Authenticator v6 では一部 Cognito エラー (NEW_PASSWORD_REQUIRED challenge 完了時の
// "Invalid attributes given, email is missing" 等) が I18n.get() を経由せず error.message が
// 直接バナーに表示される。services プロップで handleSignIn / handleConfirmSignIn をオーバーライドし、
// 既知の英語メッセージを日本語に置き換えてから throw し直すことで Authenticator のバナー表示を日本語化する。
function localizeCognitoError(error: unknown): Error {
  const msg = (error as { message?: string })?.message ?? String(error)
  if (msg.includes('email is missing')) {
    return new Error('メールアドレスが未登録のため、初回パスワード変更を完了できません。管理者にメールアドレスの登録を依頼してください。')
  }
  if (msg.includes('Invalid session for the user')) {
    return new Error('セッションの有効期限が切れました。再度ログインしてください。')
  }
  if (msg.includes('Temporary password has expired')) {
    return new Error('初期パスワードの有効期限が切れました。管理者に再発行を依頼してください。')
  }
  return error instanceof Error ? error : new Error(msg)
}

const authServices = {
  async handleSignIn(input: Parameters<typeof signIn>[0]) {
    try {
      return await signIn(input)
    } catch (error) {
      throw localizeCognitoError(error)
    }
  },
  async handleConfirmSignIn(input: Parameters<typeof confirmSignIn>[0]) {
    try {
      return await confirmSignIn(input)
    } catch (error) {
      throw localizeCognitoError(error)
    }
  },
}

const theme = {
  name: 'pams-theme',
  tokens: {
    colors: {
      brand: {
        primary: {
          10: { value: '#eff6ff' },
          20: { value: '#dbeafe' },
          40: { value: '#93c5fd' },
          60: { value: '#3b82f6' },
          80: { value: '#2563eb' },
          90: { value: '#1d4ed8' },
          100: { value: '#1e40af' },
        },
      },
    },
    components: {
      authenticator: {
        router: {
          borderWidth: { value: '0' },
          boxShadow: { value: '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)' },
          borderColor: { value: '{colors.neutral.20}' },
        },
      },
      button: {
        primary: {
          backgroundColor: { value: '{colors.brand.primary.80}' },
          _hover: {
            backgroundColor: { value: '{colors.brand.primary.90}' },
          },
        },
      },
    },
  },
}

function AuthBridge() {
  const { user, authStatus } = useAuthenticator((ctx) => [ctx.user, ctx.authStatus])
  const { login } = useAuth()
  const fetched = useRef(false)

  useEffect(() => {
    if (authStatus !== 'authenticated' || !user || fetched.current) return
    fetched.current = true

    authApi
      .loginSession()
      .then(({ data }) => {
        if (data.sessionVersion != null) {
          saveSessionVersion(data.sessionVersion)
        }
        login({
          cognitoSub: data.cognitoSub,
          municipality: data.municipality || '',
          municipalityId: data.municipalityId ?? null,
          municipalitySubdomain: data.municipalitySubdomain ?? null,
          ocrUse: data.ocrUse ?? false,
          loginId: data.loginId,
          displayName: data.displayName,
          role: data.role,
          roleDisplay: data.roleDisplay ?? '',
          permissions: data.permissions ?? [],
          sessionVersion: data.sessionVersion,
        })
      })
      .catch((err) => {
        fetched.current = false
        if (isHandledAuthError(err)) return
        void signOut().catch(() => undefined)
        window.dispatchEvent(new CustomEvent('app:pending-alert', { detail: { message: getErrorMessage(err, i18n.t('auth:profileFetchFailed')) } }))
      })
  }, [authStatus, user, login])

  return null
}

const AuthenticatorLoginScreen = () => {
  const { t } = useTranslation('common')
  const fromHost = useMunicipalityFromHost()

  if (fromHost.subdomain && fromHost.loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-100 to-blue-50 flex items-center justify-center p-6">
        <p className="text-sm text-gray-500">{t('loading')}</p>
      </div>
    )
  }

  if (fromHost.closed) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-100 to-blue-50 flex flex-col items-center justify-center p-6">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 tracking-wide mb-6">
          {t('appName')}
        </h1>
        <div className="bg-white rounded-xl shadow-lg px-10 py-8 text-center">
          <p className="text-lg font-bold text-gray-800">{t('entranceClosed')}</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-100 to-blue-50 flex flex-col items-center justify-center p-6">
      <div className="text-center mb-8">
        {fromHost.name && (
          <p className="text-xs text-gray-500 mb-1">{fromHost.name}</p>
        )}
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 tracking-wide">
          {t('appName')}
        </h1>
        <p className="text-sm text-gray-600 mt-2">{t('pleaseLogin')}</p>
      </div>
      <ThemeProvider theme={theme}>
        <Authenticator hideSignUp loginMechanisms={['username']} services={authServices}>
          <AuthBridge />
        </Authenticator>
      </ThemeProvider>
    </div>
  )
}

export default AuthenticatorLoginScreen
